import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Users, Droplets, MessageCircleQuestion, PartyPopper, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  {
    icon: Users,
    title: "Mitbestimmung",
    text: "Als Mitglied stimmen Sie in der Mitgliederversammlung mit und gestalten die Zukunft der Anlage aktiv mit.",
  },
  {
    icon: Droplets,
    title: "Wassergemeinschaft",
    text: "Sie beziehen Ihr Wasser über die Wassergemeinschaft des Vereins und profitieren von nur 30 % Abwasser anteilig vom Frischwasserbezug.",
  },
  {
    icon: MessageCircleQuestion,
    title: "Rat und Hilfe",
    text: "Sprechstunden, direkter Draht zum Vorstand und Hilfestellung bei Fragen zu Wasser, Rechnungen und Garten.",
  },
  {
    icon: PartyPopper,
    title: "Gemeinschaft",
    text: "Feste, Arbeitseinsätze und Nachbarschaft – bei uns kennen Sie Ihre Gartennachbarn beim Namen.",
  },
];

const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";

export default function Membership() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", parcel: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/membership`, form);
      toast.success("Antrag übermittelt", {
        description: "Herzlich willkommen! Der Vorstand meldet sich bei Ihnen.",
      });
      setForm({ first_name: "", last_name: "", email: "", phone: "", parcel: "", message: "" });
    } catch {
      toast.error("Übermittlung fehlgeschlagen", {
        description: "Bitte versuchen Sie es erneut oder schreiben Sie an info@kga-schoenwald.de.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="mitglied-werden" className="py-16 md:py-28" data-testid="membership-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="09"
          eyebrow="Mitglied werden"
          title="Werden Sie Teil der Gemeinschaft"
          text="Sie sind Pächterin oder Pächter in der Anlage Schönwald? Dann lohnt sich die Mitgliedschaft im Verein – für Sie und für die Gemeinschaft."
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5 content-start">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-[#D8D3C5] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#5A826B]" data-testid={`membership-benefit-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                    <b.icon size={19} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-[#1A382B]">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#47544A]">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="lg:col-span-6">
            <form onSubmit={submit} className="rounded-2xl border border-[#D8D3C5] bg-white p-6 sm:p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="membership-form">
              <h3 className="font-display text-2xl font-semibold text-[#1A382B]">Aufnahmeantrag</h3>
              <p className="mt-2 mb-6 text-sm leading-relaxed text-[#47544A]">
                Füllen Sie das Formular aus – der Vorstand meldet sich bei Ihnen und bespricht alles
                Weitere persönlich mit Ihnen.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Vorname *</label>
                  <input required className={FIELD} value={form.first_name} onChange={set("first_name")} data-testid="membership-input-first-name" />
                </div>
                <div>
                  <label className={LABEL}>Nachname *</label>
                  <input required className={FIELD} value={form.last_name} onChange={set("last_name")} data-testid="membership-input-last-name" />
                </div>
                <div>
                  <label className={LABEL}>E-Mail *</label>
                  <input required type="email" className={FIELD} value={form.email} onChange={set("email")} data-testid="membership-input-email" />
                </div>
                <div>
                  <label className={LABEL}>Telefon</label>
                  <input type="tel" className={FIELD} value={form.phone} onChange={set("phone")} data-testid="membership-input-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Parzelle (falls bereits Pächter)</label>
                  <input className={FIELD} value={form.parcel} onChange={set("parcel")} placeholder="z. B. 42" data-testid="membership-input-parcel" />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Ihre Nachricht</label>
                  <textarea rows={4} className={FIELD} value={form.message} onChange={set("message")} placeholder="Erzählen Sie kurz von sich …" data-testid="membership-input-message" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="membership-submit-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Antrag absenden
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
